import { useId, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CircleAlert } from 'lucide-react';
import { Button, Callout, Combobox, Field, NativeSelect, Sheet, statusInfo, toast } from '@elbklar/ui';
import { Task, tasksApi, TaskCreate } from '@/lib/api';
import { formatApiError } from '@/lib/validation';
import { TextareaField, TextField } from '@/components/form/Felder';
import { useAuswahlSuche } from '@/hooks/useAuswahlSuche';

// Wörter aus STATUS (`priority_…`), wie TaskCard sie zeigt.
const PRIORITAETEN = ['low', 'medium', 'high', 'urgent'].map((value) => ({ value, label: statusInfo(`priority_${value}`).label }));

interface TaskSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Zum Bearbeiten; ohne wird eine neue Aufgabe angelegt. */
  task?: Task | null;
  /** Vorbelegter Kunde, z. B. auf der Kundenseite. Ohne lässt sich einer wählen (optional). */
  customerId?: string;
  /** Vorbelegter Partner, z. B. auf der Partnerseite. Dann gibt es keine Kundenauswahl. */
  partnerId?: string;
  /** Vorbelegter Ansprechpartner, z. B. auf der Kontaktseite. */
  contactId?: string;
  /** Satz unter dem Titel, etwa „Zu Hausverwaltung Elbufer GmbH“. */
  description?: ReactNode;
  /** Kurzform fürs Handy (Schnell erfassen): Titel, Fälligkeit, Kunde. */
  kompakt?: boolean;
  onSaved?: () => void;
}

/**
 * Aufgabe anlegen oder bearbeiten: am Handy als Sheet von unten, am Rechner
 * als Dialog. Ersetzt TaskFormModal.
 */
export function TaskSheet({ open, onOpenChange, ...rest }: TaskSheetProps) {
  const formId = useId();
  const [speichert, setSpeichert] = useState(false);
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={rest.task ? 'Aufgabe bearbeiten' : 'Aufgabe anlegen'}
      description={rest.description}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={speichert}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={speichert}>
            {rest.task ? 'Aufgabe speichern' : 'Aufgabe anlegen'}
          </Button>
        </>
      }
    >
      <TaskForm formId={formId} onPendingChange={setSpeichert} onClose={() => onOpenChange(false)} {...rest} />
    </Sheet>
  );
}

function TaskForm({
  formId,
  task,
  customerId,
  partnerId,
  contactId,
  kompakt = false,
  onSaved,
  onClose,
  onPendingChange,
}: Omit<TaskSheetProps, 'open' | 'onOpenChange' | 'description'> & {
  formId: string;
  onClose: () => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const queryClient = useQueryClient();
  // Ohne Faelligkeit ist due_date null, nie "": Das Backend erwartet
  // ``date | None`` und antwortete auf den leeren String mit 422 — jede neue
  // Aufgabe ohne Datum scheiterte mit „Fehler beim Speichern" (Audit CRM-3).
  const [formData, setFormData] = useState<TaskCreate>({
    title: task?.title || '',
    description: task?.description || '',
    due_date: task?.due_date || null,
    priority: task?.priority || 'medium',
    customer_id: task?.customer_id || customerId || undefined,
    partner_id: task?.partner_id || partnerId || undefined,
    ...(contactId && !task ? { contact_id: contactId } : {}),
  });
  const [error, setError] = useState<string | null>(null);

  // Kunde wählbar nur bei einer neuen Aufgabe ohne vorgegebenen Bezug.
  const kundeWaehlbar = !task && !customerId && !partnerId;
  const kundenSuche = useAuswahlSuche('customer', kundeWaehlbar ? (formData.customer_id ?? '') : '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.title.trim()) {
      setError('Titel fehlt');
      return;
    }

    try {
      onPendingChange(true);
      if (task) {
        await tasksApi.update(task.id, formData);
        toast.success('Aufgabe gespeichert');
      } else {
        await tasksApi.create(formData);
        toast.success('Aufgabe angelegt');
      }
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['activity-feed'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onSaved?.();
      onClose();
    } catch (err) {
      // Die Meldung aus dem 422 (detail-Array) zeigen, nicht nur „Fehler".
      setError(formatApiError(err) || 'Aufgabe ließ sich nicht speichern');
      console.error(err);
    } finally {
      onPendingChange(false);
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-4">
      {error && <Callout tone="danger" icon={CircleAlert} title={error} boxed />}

      <TextField
        label="Titel"
        required
        type="text"
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        placeholder="Was zu tun ist"
      />

      <TextField
        label="Fällig am"
        type="date"
        value={formData.due_date || ''}
        onChange={(e) => setFormData({ ...formData, due_date: e.target.value || null })}
      />

      {kundeWaehlbar && (
        <Combobox
          {...kundenSuche}
          label="Kunde"
          value={formData.customer_id ?? ''}
          onChange={(id) => setFormData({ ...formData, customer_id: id || undefined })}
          placeholder="Kunde wählen"
          searchPlaceholder="Kunde suchen"
        />
      )}

      {!kompakt && (
        <>
          <Field label="Priorität">
            {(f) => (
              <NativeSelect
                {...f}
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as TaskCreate['priority'] })}
                options={PRIORITAETEN}
              />
            )}
          </Field>

          <TextareaField
            label="Beschreibung"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            placeholder="Weitere Details zur Aufgabe"
          />
        </>
      )}
    </form>
  );
}
